import { DashboardCard } from "@dashboard/components/Card";
import {
  ConfirmButton,
  type ConfirmButtonTransitionState,
} from "@dashboard/components/ConfirmButton/ConfirmButton";
import { type AtticApiKeyFragment } from "@dashboard/graphql";
import { Box, Button, Text } from "@saleor/macaw-ui-next";
import { Trash2 } from "lucide-react";
import { useIntl } from "react-intl";

import { detailMessages } from "../../messages";
import { MAX_SECRETS } from "../../utils";

type Secret = AtticApiKeyFragment["secrets"][number];

export interface AtticApiKeySecretsCardProps {
  secrets: Secret[];
  /** A revoked key: no secrets, nothing to add. */
  readOnly: boolean;
  /** A key wider than the viewer: listed, but they may not add or delete. */
  locked?: boolean;
  addSecretState: ConfirmButtonTransitionState;
  onAddSecret: () => void;
  onDeleteSecret: (secret: Secret) => void;
}

/** A key's live secrets by their last four, and the two-step rotation. */
export const AtticApiKeySecretsCard = ({
  secrets,
  readOnly,
  locked = false,
  addSecretState,
  onAddSecret,
  onDeleteSecret,
}: AtticApiKeySecretsCardProps) => {
  const intl = useIntl();
  const atLimit = secrets.length >= MAX_SECRETS;

  return (
    <DashboardCard data-test-id="attic-api-key-secrets">
      <DashboardCard.Header>
        <DashboardCard.Title>{intl.formatMessage(detailMessages.secretsTitle)}</DashboardCard.Title>
        {!readOnly && (
          <DashboardCard.Toolbar>
            <ConfirmButton
              variant="secondary"
              transitionState={addSecretState}
              // Without it the shared ConfirmButton ignores `disabled` once it
              // has been "loading" (it waits for a success or error that this
              // state never sends), so the 2-secret limit and the lock stopped
              // greying it out after the first add.
              noTransition
              disabled={atLimit || locked}
              onClick={onAddSecret}
              data-test-id="attic-api-key-add-secret"
            >
              {intl.formatMessage(detailMessages.addSecret)}
            </ConfirmButton>
          </DashboardCard.Toolbar>
        )}
      </DashboardCard.Header>
      <DashboardCard.Content>
        {!readOnly && (
          <Text as="p" size={3} color="default2" marginBottom={4}>
            {intl.formatMessage(detailMessages.secretsDescription)}
          </Text>
        )}
        {!readOnly && !locked && atLimit && (
          <Text
            as="p"
            size={3}
            color="warning1"
            marginBottom={4}
            data-test-id="attic-api-key-secret-limit"
          >
            {intl.formatMessage(detailMessages.secretLimitHint)}
          </Text>
        )}
        {secrets.length === 0 && !readOnly && (
          <Text size={3} color="default2" data-test-id="attic-api-key-no-secrets">
            {intl.formatMessage(detailMessages.noSecrets)}
          </Text>
        )}
        <Box display="flex" flexDirection="column" gap={2}>
          {secrets.map(secret => (
            <Box
              key={secret.id}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={3}
              data-test-id="attic-api-key-secret"
            >
              <Text size={3} style={{ fontFamily: "monospace" }}>
                {intl.formatMessage(detailMessages.secretEndingIn, {
                  lastFour: `••••${secret.lastFour}`,
                })}
              </Text>
              {!readOnly && (
                <Button
                  variant="tertiary"
                  icon={<Trash2 size={16} />}
                  title={intl.formatMessage(detailMessages.deleteSecret)}
                  aria-label={intl.formatMessage(detailMessages.deleteSecret)}
                  disabled={locked}
                  onClick={() => onDeleteSecret(secret)}
                  data-test-id="attic-api-key-delete-secret"
                />
              )}
            </Box>
          ))}
        </Box>
      </DashboardCard.Content>
    </DashboardCard>
  );
};

export default AtticApiKeySecretsCard;
